/**
 * Configuração i18next — internacionalização do WhiteLabelClaw
 * 
 * Padrão Big Tech: ZERO FLASH de idioma errado
 * 
 * Como funciona:
 * 1. Detecta locale da URL ANTES de inicializar (/en-US, /pt-BR, etc)
 * 2. Traduções dos 5 idiomas principais inline no bundle (sem HTTP request)
 * 3. Idiomas extras (184+) carregam via HTTP backend sob demanda
 * 4. Idioma default configurável via /admin/idiomas (fallback: en-US)
 * 
 * Features:
 * - ICU MessageFormat (pluralização avançada)
 * - Language Detector (URL > cookie > navigator > htmlTag)
 * - HTTP Backend (lazy loading para idiomas extras)
 * - RTL detection
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { isRTL } from './locales/languages';

// ===== TRADUÇÕES INLINE (bundled — zero HTTP, zero flash) =====
import ptBR_common from './locales/pt-BR/common.json';
import ptBR_auth from './locales/pt-BR/auth.json';
import ptBR_chat from './locales/pt-BR/chat.json';
import ptBR_agents from './locales/pt-BR/agents.json';
import ptBR_billing from './locales/pt-BR/billing.json';
import ptBR_admin from './locales/pt-BR/admin.json';
import ptBR_errors from './locales/pt-BR/errors.json';

import enUS_common from './locales/en-US/common.json';
import enUS_auth from './locales/en-US/auth.json';
import enUS_chat from './locales/en-US/chat.json';
import enUS_agents from './locales/en-US/agents.json';
import enUS_billing from './locales/en-US/billing.json';
import enUS_admin from './locales/en-US/admin.json';
import enUS_errors from './locales/en-US/errors.json';

import esES_common from './locales/es-ES/common.json';
import esES_auth from './locales/es-ES/auth.json';
import esES_chat from './locales/es-ES/chat.json';
import esES_agents from './locales/es-ES/agents.json';
import esES_billing from './locales/es-ES/billing.json';
import esES_admin from './locales/es-ES/admin.json';
import esES_errors from './locales/es-ES/errors.json';

import frFR_common from './locales/fr-FR/common.json';
import frFR_auth from './locales/fr-FR/auth.json';
import frFR_chat from './locales/fr-FR/chat.json';
import frFR_agents from './locales/fr-FR/agents.json';
import frFR_billing from './locales/fr-FR/billing.json';
import frFR_admin from './locales/fr-FR/admin.json';
import frFR_errors from './locales/fr-FR/errors.json';

import deDE_common from './locales/de-DE/common.json';
import deDE_auth from './locales/de-DE/auth.json';
import deDE_chat from './locales/de-DE/chat.json';
import deDE_agents from './locales/de-DE/agents.json';
import deDE_billing from './locales/de-DE/billing.json';
import deDE_admin from './locales/de-DE/admin.json';
import deDE_errors from './locales/de-DE/errors.json';

// Namespaces disponíveis
export const NAMESPACES = ['common', 'auth', 'chat', 'agents', 'billing', 'admin', 'errors'] as const;
export type Namespace = typeof NAMESPACES[number];

// Idiomas com tradução inline (bundled) — zero flash garantido
export const BUNDLED_LOCALES = ['pt-BR', 'en-US', 'es-ES', 'fr-FR', 'de-DE'] as const;

// ===== DETECÇÃO DE LOCALE VIA URL (ANTES do render) =====
// Big Tech pattern: ler a URL antes de inicializar qualquer coisa
function detectLocaleFromURL(): string | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;
  // Checa se o primeiro segmento é um locale conhecido
  const match = path.match(/^\/(pt-BR|en-US|es-ES|fr-FR|de-DE)(\/|$)/);
  return match ? match[1] : null;
}

// Detectar locale da URL ANTES de tudo (Big Tech pattern)
const urlLocale = detectLocaleFromURL();

// Recursos inline — 5 idiomas principais, zero HTTP, zero flash
const INLINE_RESOURCES = {
  'pt-BR': {
    common: ptBR_common, auth: ptBR_auth, chat: ptBR_chat,
    agents: ptBR_agents, billing: ptBR_billing, admin: ptBR_admin, errors: ptBR_errors,
  },
  'en-US': {
    common: enUS_common, auth: enUS_auth, chat: enUS_chat,
    agents: enUS_agents, billing: enUS_billing, admin: enUS_admin, errors: enUS_errors,
  },
  'es-ES': {
    common: esES_common, auth: esES_auth, chat: esES_chat,
    agents: esES_agents, billing: esES_billing, admin: esES_admin, errors: esES_errors,
  },
  'fr-FR': {
    common: frFR_common, auth: frFR_auth, chat: frFR_chat,
    agents: frFR_agents, billing: frFR_billing, admin: frFR_admin, errors: frFR_errors,
  },
  'de-DE': {
    common: deDE_common, auth: deDE_auth, chat: deDE_chat,
    agents: deDE_agents, billing: deDE_billing, admin: deDE_admin, errors: deDE_errors,
  },
};

// Temporário até carregar config do admin (en-US = safe default internacional)
const TEMP_FALLBACK = 'en-US';

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // HTTP backend para idiomas extras (não bundled)
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // 5 idiomas principais inline — disponíveis instantaneamente
    resources: INLINE_RESOURCES,

    // Se URL tem locale, usar direto (prioridade máxima)
    ...(urlLocale ? { lng: urlLocale } : {}),

    // Fallback temporário (será sobrescrito pelo admin config)
    fallbackLng: TEMP_FALLBACK,

    defaultNS: 'common',
    ns: [...NAMESPACES],
    interpolation: { escapeValue: false },

    // Detecção: cookie > navigator > htmlTag (URL é tratada acima)
    detection: {
      order: ['cookie', 'navigator', 'htmlTag'],
      caches: ['cookie'],
      cookieMinutes: 525600, // 1 ano
    },

    partialBundledLanguages: true,
    react: { useSuspense: false },
  });

// Atualizar dir=rtl/ltr quando idioma muda
i18n.on('languageChanged', (lng: string) => {
  const dir = isRTL(lng) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lng);
});

/**
 * Carregar configurações de idioma do admin (endpoint PÚBLICO, sem auth)
 * - default_locale: idioma da landing page para visitantes novos
 * - fallback_locale: idioma de fallback quando tradução não existe
 * 
 * Prioridade final: URL > Cookie > Admin Default > Admin Fallback
 */
async function loadAdminI18nSettings() {
  try {
    const resp = await fetch('/api/v1/public/i18n/settings');
    if (!resp.ok) return;
    const data = await resp.json();

    // Atualizar fallback com a config do admin
    if (data?.fallback_locale) {
      i18n.options.fallbackLng = [data.fallback_locale];
    }

    // Aplicar idioma default do admin para visitantes novos
    if (data?.default_locale && !urlLocale) {
      const cookie = document.cookie.match(/i18next=([^;]+)/);
      if (!cookie) {
        // Primeiro acesso (sem cookie) → usar default do admin
        i18n.changeLanguage(data.default_locale);
      }
    }
  } catch {
    // Silencioso — fallback temporário (en-US) já tá ativo
  }
}

// Carregar config do admin (não bloqueia render)
loadAdminI18nSettings();

export default i18n;
